import type { OfflineQueueEntry, OfflineQueueMessage, OfflineQueueSnapshotItem } from '@/lib/offline/types';
import { offlineQueueStore } from '@/stores/offlineQueueStore';

const SERVICE_WORKER_URL = '/sw.js';

const isLocalhost = () => ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname);

let listenersBound = false;

const emitQueueEvent = (message: OfflineQueueMessage) => {
  window.dispatchEvent(new CustomEvent('offline-queue-message', { detail: message }));
};

const logClientQueueEvent = (event: string, detail?: unknown) => {
  try {
    if (detail !== undefined) {
      console.info(`[offline][client] ${event}`, detail);
    } else {
      console.info(`[offline][client] ${event}`);
    }
  } catch {
    // logging must never break execution
  }
};

function determineStatusFromSnapshot(item: OfflineQueueSnapshotItem): OfflineQueueEntry['status'] {
  if (item.lastError) {
    return 'retry';
  }
  return 'queued';
}

function mapSnapshotItem(item: OfflineQueueSnapshotItem): OfflineQueueEntry {
  return {
    id: item.id,
    url: item.url,
    meta: item.meta,
    createdAt: item.createdAt,
    attempts: item.attempts,
    nextAttemptAt: item.nextAttemptAt,
    lastError: item.lastError,
    status: determineStatusFromSnapshot(item),
  };
}

async function postMessageToServiceWorker(message: unknown): Promise<void> {
  try {
    const registration = await navigator.serviceWorker.ready;
    const target = navigator.serviceWorker.controller ?? registration.active;
    target?.postMessage(message);
    logClientQueueEvent('post-message', message);
  } catch (error) {
    console.warn('[sw] unable to post message', error);
    logClientQueueEvent('post-message:error', error instanceof Error ? error.message : error);
  }
}

export async function requestOfflineQueueSnapshot(): Promise<void> {
  await postMessageToServiceWorker({ type: 'OFFLINE_QUEUE_SNAPSHOT' });
}

export async function flushOfflineQueue(): Promise<void> {
  await postMessageToServiceWorker({ type: 'OFFLINE_QUEUE_FLUSH' });
}

export async function resetOfflineQueue(): Promise<void> {
  const store = offlineQueueStore.getState();
  if (store.clear) {
    store.clear();
  } else {
    store.replaceAll([]);
  }

  if (typeof navigator === 'undefined' || !navigator.serviceWorker) {
    return;
  }

  try {
    await postMessageToServiceWorker({ type: 'OFFLINE_QUEUE_RESET' });
  } catch (error) {
    console.warn('[sw] unable to reset offline queue', error);
  }
}

function handleQueueMessage(message: OfflineQueueMessage) {
  emitQueueEvent(message);
  logClientQueueEvent(`message:${message.type.toLowerCase()}`, message.payload);
  const store = offlineQueueStore.getState();

  switch (message.type) {
    case 'QUEUE_SNAPSHOT': {
      const entries = message.payload.requests.map(mapSnapshotItem);
      store.replaceAll(entries);
      break;
    }
    case 'QUEUE_PENDING': {
      const entry: OfflineQueueEntry = {
        id: message.payload.id,
        url: message.payload.url,
        meta: message.payload.meta,
        createdAt: message.payload.createdAt,
        attempts: 0,
        status: 'queued',
      };
      store.upsert(entry);
      break;
    }
    case 'QUEUE_PROCESSING': {
      const existing = store.entries[message.payload.id];
      if (!existing) {
        store.upsert({
          id: message.payload.id,
          url: message.payload.url,
          meta: message.payload.meta,
          createdAt: Date.now(),
          attempts: message.payload.attempts,
          status: 'processing',
        });
      } else {
        store.patch(message.payload.id, {
          status: 'processing',
          attempts: message.payload.attempts,
          meta: message.payload.meta,
        });
      }
      break;
    }
    case 'QUEUE_RETRY': {
      const update: Partial<OfflineQueueEntry> = {
        status: 'retry',
        attempts: message.payload.attempts,
        nextAttemptAt: message.payload.nextAttemptAt,
        lastError: message.payload.error,
        meta: message.payload.meta,
      };
      const existing = store.entries[message.payload.id];
      if (!existing) {
        store.upsert({
          id: message.payload.id,
          url: message.payload.url,
          meta: message.payload.meta,
          createdAt: Date.now(),
          attempts: message.payload.attempts,
          status: 'retry',
          nextAttemptAt: message.payload.nextAttemptAt,
          lastError: message.payload.error,
        });
      } else {
        store.patch(message.payload.id, update);
      }
      break;
    }
    case 'QUEUE_SYNCED': {
      const update: Partial<OfflineQueueEntry> = {
        status: 'synced',
        completedAt: message.payload.completedAt,
        lastError: undefined,
        meta: message.payload.meta,
      };
      const existing = store.entries[message.payload.id];
      if (!existing) {
        store.upsert({
          id: message.payload.id,
          url: message.payload.url,
          meta: message.payload.meta,
          createdAt: Date.now(),
          attempts: 0,
          status: 'synced',
          completedAt: message.payload.completedAt,
        });
      } else {
        store.patch(message.payload.id, update);
      }
      break;
    }
    default:
      break;
  }
}

function handleServiceWorkerMessage(event: MessageEvent<unknown>) {
  const data = event.data;
  if (!data || typeof data !== 'object') {
    return;
  }

  const message = data as OfflineQueueMessage;
  if (!('type' in message)) {
    return;
  }

  handleQueueMessage(message);
}

function bindQueueListeners() {
  if (listenersBound) {
    return;
  }
  listenersBound = true;
  logClientQueueEvent('listeners:bind');

  navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    logClientQueueEvent('controller:changed');
    void requestOfflineQueueSnapshot();
    if (navigator.onLine) {
      void flushOfflineQueue();
    }
  });
  window.addEventListener('online', () => {
    logClientQueueEvent('navigator:online');
    void flushOfflineQueue();
  });

  void requestOfflineQueueSnapshot();
  if (navigator.onLine) {
    void flushOfflineQueue();
  }
}

export function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) {
    return;
  }

  if (!window.isSecureContext && !isLocalhost()) {
    return;
  }

  bindQueueListeners();

  const register = async () => {
    try {
      await navigator.serviceWorker.register(SERVICE_WORKER_URL, { type: 'module' });
      logClientQueueEvent('register:success');
      await requestOfflineQueueSnapshot();
    } catch (error) {
      console.warn('[sw] registration failed', error);
      logClientQueueEvent('register:error', error instanceof Error ? error.message : error);
    }
  };

  if (document.readyState === 'complete') {
    void register();
  } else {
    window.addEventListener('load', () => {
      void register();
    }, { once: true });
  }
}
